import { useState, type FormEvent } from "react";
import { ChapterPicker } from "@/book/components/chapter-picker";
import { LazyMarkdownEditor } from "@/book/components/lazy-editor";
import { ThankYou } from "@/book/components/thank-you";
import { errorMessage } from "@/shared/api/client";
import { useChapters, useSubmitExperience } from "@/shared/api/book";
import { Button } from "@/shared/ui/button";
import { CheckboxRow } from "@/shared/ui/checkbox";
import { ErrorNote } from "@/shared/ui/state";

/** Tapasztalat beküldése (moderálva). A szöveg **Markdownként** megy a szerverre: ugyanaz a
 *  szerkesztő formázza (félkövér, dőlt, lista, idézet, link), mint az admin `body` mezőit.
 *  A gyakorlat-oldalról a privát jegyzet előtöltve érkezik — „Megosztom tapasztalatként"
 *  (rendszerterv 8.). */
export function ExperienceForm({
  initialBody = "",
  exerciseId,
  chapterId,
  parentId,
  onDone,
}: {
  initialBody?: string;
  exerciseId?: number;
  chapterId?: number;
  parentId?: number;
  onDone: () => void;
}) {
  const [body, setBody] = useState(initialBody);
  const [chapterIds, setChapterIds] = useState<number[]>(chapterId ? [chapterId] : []);
  const [consent, setConsent] = useState(false);
  const [sent, setSent] = useState(false);
  const { data: chapters } = useChapters();
  const submit = useSubmitExperience();

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    submit.mutate(
      {
        body: body.trim(),
        book_consent: consent,
        chapter_ids: chapterIds,
        exercise_ids: exerciseId ? [exerciseId] : [],
        parent_id: parentId ?? null,
      },
      { onSuccess: () => setSent(true) },
    );
  };

  const isReply = parentId !== undefined;
  // Siker után a panel nem záródik be szó nélkül: köszönet + mi történik ezután.
  if (sent) {
    return isReply ? (
      <ThankYou title="Köszönjük a válaszod!" onDone={onDone}>
        Jóváhagyás után a szálban mindenki látja. Addig csak te.
      </ThankYou>
    ) : (
      <ThankYou title="Köszönjük, hogy megosztottad!" onDone={onDone}>
        Jóváhagyás után a többi olvasó is látja a tapasztalatodat. Addig a listában
        „jóváhagyásra vár” jelöléssel csak te.
      </ThankYou>
    );
  }
  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4">
      <LazyMarkdownEditor
        label={isReply ? "A válaszod" : "A tapasztalatod"}
        value={body}
        onChange={setBody}
        placeholder="Mi történt, mit próbáltál, mit hozott?"
        rows={6}
      />

      {!isReply && chapters && chapters.length > 0 && (
        <ChapterPicker
          label="Melyik fejezet(ek)hez kapcsolódik?"
          chapters={chapters}
          value={chapterIds}
          onChange={setChapterIds}
        />
      )}

      {!isReply && (
        <CheckboxRow id="exp-consent" checked={consent} onCheckedChange={setConsent}>
          Megjelenhet a könyv későbbi kiadásaiban is
        </CheckboxRow>
      )}

      <p className="text-[12px] leading-[1.4] text-muted">
        A beküldött tapasztalat jóváhagyás után jelenik meg — addig csak te látod.
      </p>

      {submit.isError && <ErrorNote>{errorMessage(submit.error)}</ErrorNote>}

      <Button type="submit" block size="lg" disabled={submit.isPending || body.trim().length < 10}>
        {submit.isPending ? "Küldés…" : "Beküldöm"}
      </Button>
    </form>
  );
}
